import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCardModule } from '@angular/material/card';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

//import { HistoryService } from 'apps/healthtrackapp/src/app/core/services/history.service';
import { MailService } from '../services/mail.service';
import { ConfirmedValidator } from '@nx-ahs/shared/validators';
import { ConfirmationModalService, ModalTemplateComponent, AppSpinnerComponent } from '@nx-ahs/shared/components';
import { MomentPipe } from '@nx-ahs/shared/pipes';
import { ComplianceDateChangeReason } from '../Models/ComplianceDateChangeReason';
import { MailBatchDetail } from '../Models/MailBatchDetail';
import { AhsTranslationService } from '@nx-ahs/base-translate';

@Component({
	selector: 'app-edit-batch',
	templateUrl: './edit-batch.component.html',
	styleUrls: ['./edit-batch.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatDatepickerModule,
		MatCardModule,
		ModalTemplateComponent,
		AppSpinnerComponent,
		MomentPipe
	]
})
export class EditBatchComponent implements OnInit {
	public title: string;
	public loading: boolean = true;
	public config: any;
	public formGroup: FormGroup;
	public showSave: boolean = true;
	public showCancel: boolean = true;

	public batchHistory: any[];
	public batchLabelHistory: any[];
	public batchComplianceDateHistory: any[];
	public batchChangeReasonHistory: any[];

	public mailBatchId: number;
	public entityNameId: number;
	public complianceDateChangeReasons: ComplianceDateChangeReason[];
	public mailBatchDetail: any;
	public today: Date = new Date();

	private historyPromise: any;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: any,
		@Inject('historyService') private historyService: any,
		public matDialogRef: MatDialogRef<EditBatchComponent>,
		private mailService: MailService,
		//private historyService: HistoryService,
		private formBuilder: FormBuilder,
		private confirmationModalService: ConfirmationModalService,
		private translateService: AhsTranslationService
	) {
		this.mailBatchId = data['mailBatchId'];
		this.entityNameId = 58;
	}

	ngOnInit(): void {
		this.loading = true;

		// Set form title
		this.title = 'BatchId ' + this.mailBatchId.toString() + ' Details';

		// Create form
		this.formGroup = this.formBuilder.group({
			labelText: ['', [Validators.required]],
			complianceDate: [null, [Validators.required]],
			complianceDateChangeReason: null
		});

		this.mailService
			.getComplianceDateChangeReasons()
			.toPromise()
			.then((results) => {
				this.complianceDateChangeReasons = results;

				this.loadBatchHistory()
					.then(() => {
						this.loadBatchDetail(this.mailBatchId).then((mailBatchDetail) => {
							if (mailBatchDetail) {
								this.mailBatchDetail = mailBatchDetail[0];

								this.formGroup.controls['labelText'].setValue(this.mailBatchDetail.label);
								this.formGroup.controls['complianceDate'].setValue(this.mailBatchDetail.complianceDate);
								this.formGroup.controls['complianceDateChangeReason'].setValue(
									this.mailBatchDetail.complianceDateChangeReasonId
								);
							}
						});
					})
					.finally(() => {
						this.loading = false;
					});
			});

		this.matDialogRef.keydownEvents().subscribe((event) => {
			if (event.key === 'Escape') {
				this.cancelClick();
			}
		});

		this.matDialogRef.backdropClick().subscribe(() => {
			this.cancelClick();
		});

		this.matDialogRef.disableClose = true;
	}

	public complianceDateChanged() {
		if (this.mailBatchDetail.complianceDate != this.formGroup.controls['complianceDate'].value) {
			this.formGroup.controls.complianceDateChangeReason.setValidators([Validators.required]);

			this.formGroup.controls.complianceDateChangeReason.markAsTouched();
			this.formGroup.controls.complianceDateChangeReason.updateValueAndValidity();
		}
	}

	public cancelClick() {
		this.confirmationModalService
			.showConfirmationDialog(
				this.translateService.translate.instant('Confirm'),
				this.translateService.translate.instant('Click Ok to continue'),
				false,
				true
			)
			.beforeClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					this.matDialogRef.close(false);
				}
			});
	}

	public saveClick() {
		if (this.formGroup.valid) {
			this.loading = true;

			this.mailBatchDetail.label = this.formGroup.controls['labelText'].value;
			this.mailBatchDetail.complianceDate = this.formGroup.controls['complianceDate'].value;
			this.mailBatchDetail.complianceDateChangeReasonId =
				this.formGroup.controls['complianceDateChangeReason'].value;

			this.mailService
				.updateMailBatchDetail(this.mailBatchDetail)
				.toPromise()
				.then(() => {
					this.loadBatchHistory();
				})
				.finally(() => {
					this.loading = false;
					this.matDialogRef.close(true);
				});
		}
	}

	private loadBatchDetail(mailBatchId: number): Promise<MailBatchDetail> {
		return this.mailService.getBatchEditDetail(mailBatchId);
	}

	private loadBatchHistory(): Promise<any> {
		return Promise.all([this.historyPromise, this.loadLabelChanges()]).then((result) => {
			Promise.all([this.loadComplianceDateChanges(), this.loadChangeReasonChanges()]).then(() => {
				this.batchHistory = this.batchLabelHistory.concat(
					this.batchComplianceDateHistory,
					this.batchChangeReasonHistory
				);
				this.sortHistory();
			});
		});
	}

	private loadLabelChanges(): Promise<any> {
		return Promise.all([
			this.historyPromise,
			this.historyService.getFieldHistory(this.entityNameId, this.mailBatchId, 'label').toPromise()
		]).then((results) => {
			// Store the results
			const metadata = results[0];
			this.batchLabelHistory = results[1];

			// Loop through each result and set values
			for (let i = 0; i < this.batchLabelHistory?.length; i++) {
				this.batchLabelHistory[i].fieldName = 'Batch Label';

				// Set the old value
				for (let x = i + 1; x < this.batchLabelHistory?.length; x++) {
					if (this.batchLabelHistory[i].validFrom == this.batchLabelHistory[x].validTo) {
						this.batchLabelHistory[i].previousValue = this.batchLabelHistory[x].field;
					}
				}
			}
		});
	}

	private loadComplianceDateChanges(): Promise<any> {
		return Promise.all([
			this.historyPromise,
			this.historyService.getFieldHistory(this.entityNameId, this.mailBatchId, 'complianceDate').toPromise()
		]).then((results) => {
			// Store the results
			const metadata = results[0];
			this.batchComplianceDateHistory = results[1];

			for (let i = 0; i < this.batchComplianceDateHistory?.length; i++) {
				this.batchComplianceDateHistory[i].fieldName = 'Compliance Date';

				// Set the old value
				for (let x = i + 1; x < this.batchComplianceDateHistory?.length; x++) {
					if (this.batchComplianceDateHistory[i].validFrom == this.batchComplianceDateHistory[x].validTo) {
						this.batchComplianceDateHistory[i].previousValue = this.batchComplianceDateHistory[x].field;
					}
				}

				// Set the dates to a more readable format
				this.batchComplianceDateHistory[i].field = new Date(this.batchComplianceDateHistory[i].field);
				if (this.batchComplianceDateHistory[i].previousValue) {
					this.batchComplianceDateHistory[i].previousValue = new Date(
						this.batchComplianceDateHistory[i].previousValue
					);
				}
			}
		});
	}

	private loadChangeReasonChanges(): Promise<any> {
		return Promise.all([
			this.historyPromise,
			this.historyService
				.getFieldHistory(this.entityNameId, this.mailBatchId, 'complianceDateChangeReasonId')
				.toPromise()
		]).then((results) => {
			// Store the results
			const metadata = results[0];
			this.batchChangeReasonHistory = results[1];

			// Loop through each result and set values
			for (let i = 0; i < this.batchChangeReasonHistory?.length; i++) {
				this.batchChangeReasonHistory[i].fieldName = 'Compliance Date Change Reason';
				//set the old value
				for (let x = i + 1; x < this.batchChangeReasonHistory?.length; x++) {
					if (this.batchChangeReasonHistory[i].validFrom == this.batchChangeReasonHistory[x].validTo) {
						this.batchChangeReasonHistory[i].previousValue = this.batchChangeReasonHistory[x].field;
					}
				}

				//set the new value
				for (let r = 0; r < this.complianceDateChangeReasons.length; r++) {
					if (
						this.complianceDateChangeReasons[r].complianceDateChangeReasonId ==
						this.batchChangeReasonHistory[i].field
					) {
						this.batchChangeReasonHistory[i].field = this.complianceDateChangeReasons[r].description;
					}
					if (
						this.batchChangeReasonHistory[r].complianceDateChangeReasonId ==
						this.batchChangeReasonHistory[i].previousValue
					) {
						this.batchChangeReasonHistory[i].previousValue =
							this.complianceDateChangeReasons[r].description;
					}
				}
			}
		});
	}

	private sortHistory() {
		this.batchHistory.sort(function (a, b) {
			const x = a.validFrom;
			const y = b.validFrom;
			if (x > y) {
				return -1;
			}
			if (x < y) {
				return 1;
			}
			return 0;
		});
	}
}
